import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from 'src/lib/utils';
import {
  PortalDsAlert,
  PortalDsBadge,
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSectionHeader,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  useBulkLogEntryActionMutation,
  useCreateLogLinearIssueMutation,
  useGetLogEntriesQuery,
  useGetLogEntryQuery,
  useGetLogMonitoringStatsQuery,
  useGetLogScanRunsQuery,
  useIgnoreLogEntryMutation,
  useMarkLogEntryFixedMutation,
  useRestoreLogEntryMutation,
  useScanLogsNowMutation,
} from 'src/redux/operations/operations.endpoints';
import { formatSchedulerMutationError } from 'src/helpers/scheduler-schedule.helpers';
import type { LogEntryBulkFilters, LogEntrySummary } from '../types/log-monitoring.types';
import { formatLogEntryFrequency } from '../helpers/log-monitoring-formatters';
import { LogEntryDetailSheet } from './log-entry-detail-sheet';
import { LogMonitoringStatsTiles } from './log-monitoring-stats-tiles';
import { EtlRunsPagination } from './etl-runs-pagination';

export const LOG_ENTRIES_PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'linear_linked', label: 'Linear linked' },
  { value: 'ignored', label: 'Ignored (filtered)' },
  { value: 'fixed', label: 'Fixed' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'error', label: 'Error' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const SERVICE_OPTIONS = [
  { value: '', label: 'All services' },
  { value: 'invisibly-portal-api-qa', label: 'invisibly-portal-api-qa' },
  { value: 'invisibly-portal-api-prod', label: 'invisibly-portal-api-prod' },
  { value: 'self-service-api-qa', label: 'self-service-api-qa' },
  { value: 'self-service-api-prod', label: 'self-service-api-prod' },
];

function severityVariant(severity: string): 'red' | 'amber' | 'blue' | 'gray' {
  if (severity === 'critical' || severity === 'error') return 'red';
  if (severity === 'warning') return 'amber';
  if (severity === 'info') return 'blue';
  return 'gray';
}

function formatRelative(value: string) {
  return new Date(value).toLocaleString();
}

export function LogMonitoringSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLogId = searchParams.get('log');

  const [statusFilter, setStatusFilter] = useState('new');
  const [severityFilter, setSeverityFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, severityFilter, serviceFilter, search]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllFiltered(false);
  }, [statusFilter, severityFilter, serviceFilter, search, page]);

  const currentFilters = useMemo((): LogEntryBulkFilters => ({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(severityFilter ? { severity: severityFilter } : {}),
    ...(serviceFilter ? { service: serviceFilter } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  }), [search, serviceFilter, severityFilter, statusFilter]);

  const listParams = useMemo(() => ({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(severityFilter ? { severity: severityFilter } : {}),
    ...(serviceFilter ? { service: serviceFilter } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ordering: '-last_seen_at',
    page,
    page_size: LOG_ENTRIES_PAGE_SIZE,
  }), [page, search, serviceFilter, severityFilter, statusFilter]);

  const {
    data: entriesPage,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetLogEntriesQuery(listParams, { pollingInterval: 60_000 });

  const { data: stats, isLoading: isStatsLoading } = useGetLogMonitoringStatsQuery(undefined, {
    pollingInterval: 60_000,
  });
  const { data: scanRuns = [] } = useGetLogScanRunsQuery(undefined, {
    pollingInterval: 30_000,
  });

  const {
    data: selectedEntry,
    isLoading: isEntryLoading,
  } = useGetLogEntryQuery(
    { entryId: selectedLogId ?? '' },
    { skip: !selectedLogId, pollingInterval: selectedLogId ? 30_000 : 0 },
  );

  const [scanNow, { isLoading: isScanning }] = useScanLogsNowMutation();
  const [ignoreEntry, { isLoading: isIgnoring }] = useIgnoreLogEntryMutation();
  const [markFixed, { isLoading: isMarkingFixed }] = useMarkLogEntryFixedMutation();
  const [createLinear, { isLoading: isCreatingLinear }] = useCreateLogLinearIssueMutation();
  const [restoreEntry, { isLoading: isRestoring }] = useRestoreLogEntryMutation();
  const [bulkAction, { isLoading: isBulkActioning }] = useBulkLogEntryActionMutation();

  const scanInProgress = scanRuns.some(
    (run) => run.status === 'running' || run.status === 'pending',
  );

  const lastScan = scanRuns[0];
  const entries = entriesPage?.items ?? [];
  const isActioning = isIgnoring || isMarkingFixed || isCreatingLinear || isRestoring;
  const pageIds = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const allPageSelected = selectAllFiltered
    || (pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id)));
  const hasSelection = selectAllFiltered || selectedIds.size > 0;
  const selectedCount = selectAllFiltered
    ? (entriesPage?.total_objects ?? 0)
    : selectedIds.size;

  const togglePageSelection = () => {
    if (allPageSelected) {
      clearSelection();
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleRowSelection = (entryId: string) => {
    if (selectAllFiltered) {
      clearSelection();
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllFiltered(false);
  };

  const openEntry = (entry: LogEntrySummary) => {
    const next = new URLSearchParams(searchParams);
    next.set('log', entry.id);
    setSearchParams(next);
  };

  const closeEntry = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('log');
    setSearchParams(next);
  };

  const handleScanNow = async () => {
    setActionError(null);
    try {
      await scanNow().unwrap();
      refetch();
    } catch (error) {
      setActionError(formatSchedulerMutationError(error));
    }
  };

  const runBulkAction = async (action: 'ignore' | 'mark_fixed') => {
    setActionError(null);
    try {
      await bulkAction({
        action,
        ...(selectAllFiltered
          ? { select_all: true, filters: currentFilters }
          : { ids: [...selectedIds], select_all: false }),
      }).unwrap();
      clearSelection();
      refetch();
    } catch (error) {
      setActionError(formatSchedulerMutationError(error));
    }
  };

  const runAction = async (action: () => Promise<unknown>) => {
    if (!selectedLogId) return;
    setActionError(null);
    try {
      await action();
      refetch();
    } catch (error) {
      setActionError(formatSchedulerMutationError(error));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <LogMonitoringStatsTiles stats={stats} isLoading={isStatsLoading} />

      <PortalDsCard padding={false}>
        <div className="flex flex-col gap-4 p-5 sm:p-6">
          <PortalDsSectionHeader
            overline="Operations"
            title="Monitor"
            description="GCP Cloud Run errors and warnings triaged by scheduled scans."
            actions={(
              <div className="flex flex-wrap items-center gap-2">
                <span className={portalDs.caption}>
                  {lastScan
                    ? `Last scan: ${lastScan.service} · ${lastScan.status} · ${formatRelative(lastScan.scheduled_at)}`
                    : 'No scans yet'}
                </span>
                <PortalDsButton
                  variant="accent"
                  size="sm"
                  disabled={scanInProgress || isScanning}
                  onClick={handleScanNow}
                >
                  {scanInProgress || isScanning ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RefreshCw className="size-4" />
                  )}
                  Scan now
                </PortalDsButton>
              </div>
            )}
          />

          {actionError && (
            <PortalDsAlert variant="danger">{actionError}</PortalDsAlert>
          )}

          <div className="flex flex-wrap gap-3">
            <PortalDsField label="Status" htmlFor="log-filter-status" filter>
              <PortalDsSelect
                id="log-filter-status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Severity" htmlFor="log-filter-severity" filter>
              <PortalDsSelect
                id="log-filter-severity"
                value={severityFilter}
                onChange={(event) => setSeverityFilter(event.target.value)}
              >
                {SEVERITY_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Service" htmlFor="log-filter-service" filter>
              <PortalDsSelect
                id="log-filter-service"
                value={serviceFilter}
                onChange={(event) => setServiceFilter(event.target.value)}
              >
                {SERVICE_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsField label="Search" htmlFor="log-filter-search" filter>
              <PortalDsInput
                id="log-filter-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Summary or message"
              />
            </PortalDsField>
          </div>

          {isError && (
            <PortalDsAlert variant="danger">Failed to load log entries.</PortalDsAlert>
          )}

          {hasSelection && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3">
              <span className={portalDs.bodySm}>
                {selectedCount}
                {' '}
                {selectedCount === 1 ? 'entry' : 'entries'}
                {' '}
                selected
              </span>
              <PortalDsButton
                variant="secondary"
                size="sm"
                disabled={isBulkActioning}
                onClick={() => runBulkAction('ignore')}
              >
                Ignore
              </PortalDsButton>
              <PortalDsButton
                variant="secondary"
                size="sm"
                disabled={isBulkActioning}
                onClick={() => runBulkAction('mark_fixed')}
              >
                Mark fixed
              </PortalDsButton>
              <button
                type="button"
                className={cn(portalDs.link, 'text-sm')}
                onClick={clearSelection}
              >
                Clear selection
              </button>
            </div>
          )}

          {allPageSelected
            && !selectAllFiltered
            && entriesPage
            && entriesPage.total_objects > entries.length && (
            <div className="rounded-lg border border-[#EDEEF2] bg-[#FFFBEB] px-4 py-3 text-sm text-[#3D4155]">
              All
              {' '}
              {entries.length}
              {' '}
              entries on this page are selected.
              {' '}
              <button
                type="button"
                className={cn(portalDs.link, 'font-medium')}
                onClick={() => setSelectAllFiltered(true)}
              >
                Select all
                {' '}
                {entriesPage.total_objects}
                {' '}
                entries matching current filters
              </button>
            </div>
          )}

          {selectAllFiltered && entriesPage && (
            <div className="rounded-lg border border-[#EDEEF2] bg-[#FFFBEB] px-4 py-3 text-sm text-[#3D4155]">
              All
              {' '}
              {entriesPage.total_objects}
              {' '}
              entries matching current filters are selected.
              {' '}
              <button
                type="button"
                className={cn(portalDs.link, 'font-medium')}
                onClick={clearSelection}
              >
                Clear selection
              </button>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-[#EDEEF2]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-gray-300"
                      checked={allPageSelected}
                      aria-label="Select all entries on this page"
                      onChange={togglePageSelection}
                    />
                  </TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-[#5E6278]">
                      <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
                      Loading entries…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-[#5E6278]">
                      No log entries match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && entries.map((entry) => (
                  <TableRow
                    key={entry.id}
                    className="cursor-pointer hover:bg-[#F7F8FA]"
                    onClick={() => openEntry(entry)}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="size-4 rounded border-gray-300"
                        checked={selectAllFiltered || selectedIds.has(entry.id)}
                        aria-label={`Select ${entry.llm_summary}`}
                        onChange={() => toggleRowSelection(entry.id)}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className="max-w-xs truncate font-medium">
                      {entry.llm_summary}
                    </TableCell>
                    <TableCell>{entry.service}</TableCell>
                    <TableCell>
                      <PortalDsBadge variant={severityVariant(entry.severity)}>
                        {entry.severity}
                      </PortalDsBadge>
                    </TableCell>
                    <TableCell>{entry.category}</TableCell>
                    <TableCell className="whitespace-nowrap text-[#5E6278]">
                      <div className="flex items-center gap-2">
                        <span>
                          {formatLogEntryFrequency(entry.occurrence_count, entry.last_seen_at)}
                        </span>
                        {entry.occurrence_count > 1 && (
                          <PortalDsBadge variant="amber">
                            Repeat ×
                            {entry.occurrence_count}
                          </PortalDsBadge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{entry.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {entriesPage && (
            <EtlRunsPagination
              page={entriesPage.page}
              totalPages={entriesPage.total_pages}
              totalObjects={entriesPage.total_objects}
              pageSize={entriesPage.page_size}
              onPageChange={setPage}
              disabled={isFetching}
            />
          )}
        </div>
      </PortalDsCard>

      {selectedLogId && (
        <LogEntryDetailSheet
          entry={selectedEntry}
          isLoading={isEntryLoading}
          isActioning={isActioning}
          onClose={closeEntry}
          onIgnore={() => runAction(() => ignoreEntry({ entryId: selectedLogId }).unwrap())}
          onMarkFixed={() => runAction(() => markFixed({ entryId: selectedLogId }).unwrap())}
          onCreateLinearIssue={() => runAction(() => createLinear({ entryId: selectedLogId }).unwrap())}
          onRestore={() => runAction(() => restoreEntry({ entryId: selectedLogId }).unwrap())}
        />
      )}
    </div>
  );
}
